import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

interface PopoverProps {
  trigger: React.ReactNode
  children: React.ReactNode
  align?: 'left' | 'right' | 'center'
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

/**
 * Anchored popover portaled to `document.body`.
 *
 * Rendered with fixed positioning so it is never clipped by an ancestor's
 * `overflow: hidden` (todo rows live inside scrolling day columns). The panel
 * is aligned to the trigger per `align` and then clamped into the viewport.
 */
export function Popover({ trigger, children, align = 'left', open: controlledOpen, onOpenChange }: PopoverProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen

  const setOpen = (value: boolean) => {
    if (!isControlled) setInternalOpen(value)
    onOpenChange?.(value)
  }

  const triggerRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [coords, setCoords] = useState({ x: 0, y: 0 })

  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !panelRef.current) return
    const anchor = triggerRef.current.getBoundingClientRect()
    const panel = panelRef.current.getBoundingClientRect()
    const pad = 8

    let x =
      align === 'right' ? anchor.right - panel.width :
      align === 'center' ? anchor.left + anchor.width / 2 - panel.width / 2 :
      anchor.left
    let y = anchor.bottom + 4

    if (x + panel.width > window.innerWidth - pad) x = window.innerWidth - panel.width - pad
    if (x < pad) x = pad
    if (y + panel.height > window.innerHeight - pad) y = Math.max(pad, anchor.top - panel.height - 4)
    setCoords({ x, y })
  }, [open, align])

  useEffect(() => {
    if (!open) return
    function handleMouseDown(e: MouseEvent) {
      const t = e.target as Node
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return
      setOpen(false)
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleMouseDown, true)
    document.addEventListener('keydown', handleKey, true)
    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true)
      document.removeEventListener('keydown', handleKey, true)
    }
  }, [open])

  return (
    <>
      <div ref={triggerRef} style={{ display: 'inline-block' }} onClick={() => setOpen(!open)}>
        {trigger}
      </div>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: 'fixed',
              left: coords.x,
              top: coords.y,
              zIndex: 10000,
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-md)',
              minWidth: '200px',
              maxWidth: 'min(320px, calc(100vw - 16px))',
            }}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {children}
          </div>,
          document.body
        )}
    </>
  )
}
